/* ShinySwipe Theme — Main JS */
(() => {
  'use strict';

  /* ── Performance / accessibility guard ── */
  const SS_PRM  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SS_LITE = SS_PRM || (typeof navigator.deviceMemory !== 'undefined' && navigator.deviceMemory < 4) || window.innerWidth <= 768;
  if (SS_LITE) document.documentElement.classList.add('ss-lite');

  /* ── Sticky header shadow ── */
  const header = document.getElementById('site-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Mobile menu toggle ── */
  const menuToggle = document.getElementById('menu-toggle');
  const mobileNav  = document.getElementById('mobile-nav');
  let _navPrevFocus = null;
  if (menuToggle && mobileNav) {
    menuToggle.addEventListener('click', () => {
      const isOpen = mobileNav.getAttribute('aria-hidden') === 'false';
      if (isOpen) {
        mobileNav.setAttribute('aria-hidden', 'true');
        menuToggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        _navPrevFocus?.focus();
        _navPrevFocus = null;
      } else {
        mobileNav.setAttribute('aria-hidden', 'false');
        menuToggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        _navPrevFocus = document.activeElement;
        requestAnimationFrame(() => {
          mobileNav.querySelector('a, button:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus();
        });
      }
    });
  }

  /* ── Search toggle ── */
  const searchToggle = document.getElementById('search-toggle');
  const searchBar    = document.getElementById('search-bar');
  if (searchToggle && searchBar) {
    searchToggle.addEventListener('click', () => {
      const isHidden = searchBar.getAttribute('aria-hidden') === 'true';
      searchBar.setAttribute('aria-hidden', isHidden ? 'false' : 'true');
      if (isHidden) searchBar.querySelector('input')?.focus();
    });
  }

  /* ── Cart drawer ── */
  const cartDrawer   = document.getElementById('cart-drawer');
  const cartToggle   = document.getElementById('cart-toggle');
  const cartClose    = cartDrawer?.querySelector('.cart-drawer__close');
  const cartOverlay  = cartDrawer?.querySelector('.cart-drawer__overlay');

  let _cartPrevFocus = null;

  function openCart() {
    cartDrawer?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    _cartPrevFocus = document.activeElement;
    requestAnimationFrame(() => {
      const first = cartDrawer?.querySelector('button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])');
      first?.focus();
    });
  }
  function closeCart() {
    cartDrawer?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    _cartPrevFocus?.focus();
    _cartPrevFocus = null;
  }

  cartToggle?.addEventListener('click', openCart);
  cartClose?.addEventListener('click', closeCart);
  cartOverlay?.addEventListener('click', closeCart);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      if (cartDrawer?.getAttribute('aria-hidden') === 'false') { closeCart(); return; }
      if (mobileNav?.getAttribute('aria-hidden') === 'false') {
        mobileNav.setAttribute('aria-hidden', 'true');
        menuToggle?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        _navPrevFocus?.focus(); _navPrevFocus = null;
        return;
      }
    }
    if (e.key === 'Tab' && cartDrawer?.getAttribute('aria-hidden') === 'false') {
      const focusable = [...(cartDrawer?.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) || [])];
      if (focusable.length < 2) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if (e.key === 'Tab' && mobileNav?.getAttribute('aria-hidden') === 'false') {
      const focusable = [...(mobileNav?.querySelectorAll(
        'a, button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || [])];
      if (focusable.length < 2) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ── Helpers ── */
  const fmt = cents => '$' + (cents / 100).toFixed(2);

  function lamKey(variantTitle) {
    const t = (variantTitle || '').toLowerCase();
    if (t.includes('star') && t.includes('gift')) return 'star-gift';
    if (t.includes('star')) return 'star';
    if (t.includes('gift')) return 'gift';
    return 'clear';
  }

  /* ── Cart count badge ── */
  function updateCartCount(count) {
    const badge = document.getElementById('cart-count');
    const btn   = document.getElementById('cart-toggle');
    if (!btn) return;
    if (count > 0) {
      if (!badge) {
        const el = document.createElement('span');
        el.id = 'cart-count';
        el.className = 'site-header__cart-count';
        el.textContent = count;
        btn.appendChild(el);
      } else {
        badge.textContent = count;
      }
    } else {
      badge?.remove();
    }
    document.body.dataset.cartCount = count;
  }

  /* ── Sparkle particle burst ── */
  function spawnParticles(btn) {
    if (SS_LITE) return;
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const symbols = ['✦', '✨', '⭐', '★', '✦', '✨', '⭐', '★', '✦', '✨'];
    symbols.forEach((sym, i) => {
      const el = document.createElement('span');
      el.textContent = sym;
      el.style.cssText = [
        'position:fixed', 'pointer-events:none', 'z-index:99999',
        'font-size:' + (12 + Math.random() * 10) + 'px', 'line-height:1',
        'left:' + cx + 'px', 'top:' + cy + 'px',
        'transform:translate(-50%,-50%)'
      ].join(';');
      document.body.appendChild(el);
      const angle  = (i / symbols.length) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
      const dist   = 45 + Math.random() * 65;
      const tx     = Math.cos(angle) * dist;
      const ty     = Math.sin(angle) * dist;
      const rot    = (Math.random() - 0.5) * 360;
      el.animate([
        { transform: 'translate(-50%,-50%) scale(1) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0) rotate(${rot}deg)`, opacity: 0 }
      ], { duration: 650 + Math.random() * 200, easing: 'cubic-bezier(0,.9,.57,1)', fill: 'forwards' })
        .addEventListener('finish', () => el.remove());
    });

    // Button pop
    btn.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(0.93)' },
      { transform: 'scale(1.05)' },
      { transform: 'scale(1)' }
    ], { duration: 380, easing: 'ease' });
  }

  /* ── Full cart drawer re-render ── */
  function renderCartDrawer(cart) {
    updateCartCount(cart.item_count);

    const body  = cartDrawer?.querySelector('.cart-drawer__body');
    const panel = cartDrawer?.querySelector('.cart-drawer__panel');
    if (!body) return;

    // Update drawer title
    const titleEl = cartDrawer?.querySelector('.cart-drawer__title');
    if (titleEl) titleEl.textContent = 'Your Cart (' + cart.item_count + ')';

    if (cart.item_count === 0) {
      body.innerHTML = [
        '<div class="cart-drawer__empty">',
        '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
        '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>',
        '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>',
        '</svg>',
        '<p>Your cart is empty</p>',
        '<a href="/collections/all" class="btn btn--primary">Start Shopping</a>',
        '</div>'
      ].join('');
      const oldFooter = cartDrawer?.querySelector('.cart-drawer__footer');
      if (oldFooter) oldFooter.remove();
      return;
    }

    const editIcon = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
    const removeIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

    // Preserve upsell container before wipe
    const _prevUpsell = body.querySelector('#cart-upsell');
    const _savedUpsell = _prevUpsell ? _prevUpsell.outerHTML : null;

    body.innerHTML = cart.items.map((item, i) => {
      const line = i + 1;
      const p = item.properties || {};
      const isBundle = !!p['Card 1'];
      const variantLabel = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<p class="cart-item__variant">' + item.variant_title + '</p>' : '';

      // Image or bundle placeholder
      let imgHtml;
      if (!item.image) {
        imgHtml = '<div class="cart-item__bundle-placeholder">🃏</div>';
      } else {
        imgHtml = '<a href="' + item.url + '"><img src="' + item.image +
          '" class="cart-item__image" loading="lazy" onerror="this.style.display=\'none\'" alt="' +
          (item.product_title || '').replace(/"/g, '&quot;') + '"></a>';
      }

      // Bundle card list + edit button
      let bundleHtml = '';
      if (isBundle) {
        const cards = [1, 2, 3, 4].map(n => {
          const v = p['Card ' + n];
          return v ? '<div class="cart-item__bundle-card"><span class="cart-item__bundle-card-num">' + n + '.</span>' + v.replace(/</g, '&lt;') + '</div>' : '';
        }).join('');

        const ep = [1,2,3,4].map(n => 'p' + n + '=' + encodeURIComponent(p['_id' + n] || '') + '&l' + n + '=' + encodeURIComponent(p['_lam' + n] || 'clear')).join('&');
        const editUrl = '/pages/bundles?' + ep + '&replace_key=' + encodeURIComponent(item.key);

        bundleHtml = '<div class="cart-item__bundle-cards">' + cards + '</div>' +
          '<a href="' + editUrl + '" class="cart-item__edit">' + editIcon + ' Edit bundle</a>';
      }

      return '<div class="cart-item" data-line="' + line + '">' +
        imgHtml +
        '<div class="cart-item__details">' +
          '<p class="cart-item__title">' + item.product_title + '</p>' +
          variantLabel +
          '<p class="cart-item__price">' + fmt(item.final_price) + '</p>' +
          bundleHtml +
          '<div class="cart-item__quantity">' +
            '<button class="qty-btn" data-action="decrease" data-line="' + line + '" aria-label="Decrease quantity">&#8722;</button>' +
            '<span class="qty-value">' + item.quantity + '</span>' +
            '<button class="qty-btn" data-action="increase" data-line="' + line + '" aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<button class="cart-item__remove" data-line="' + line + '" aria-label="Remove item">' + removeIcon + '</button>' +
        '</div>';
    }).join('');

    // Re-inject upsell after body wipe
    if (cart.item_count > 0) {
      if (_savedUpsell) {
        body.insertAdjacentHTML('beforeend', _savedUpsell);
      } else {
        body.insertAdjacentHTML('beforeend',
          '<div class="cart-upsell" id="cart-upsell" style="display:none;">' +
          '<p class="cart-upsell__heading">You might also like</p>' +
          '<div class="cart-upsell__grid" id="cart-upsell-grid"></div>' +
          '</div>');
      }
      if (typeof window.ssInitCartUpsell === 'function') {
        window.ssInitCartUpsell(cart.items.map(function(i){ return i.product_id; }));
      }
    }

    // Ensure footer element exists
    let footer = cartDrawer?.querySelector('.cart-drawer__footer');
    if (!footer && panel) {
      footer = document.createElement('div');
      footer.className = 'cart-drawer__footer';
      panel.appendChild(footer);
    }
    if (!footer) return;

    const total          = cart.total_price;
    const FREE_CENTS     = 3598;
    const TRACKED_CENTS  = 5397;
    // Use line-price subtotal (before order-level discounts) for shipping threshold
    const barTotal       = cart.items.reduce((s, i) => s + (i.final_line_price || 0), 0);
    const freeRem        = FREE_CENTS - barTotal;
    const trackedRem     = TRACKED_CENTS - barTotal;

    // B3G3 progress bar
    const b3Count = cart.item_count;
    const b3Unlocked = b3Count >= 3;
    const mkSlot = (i) => {
      if (i < 3) {
        const f = b3Count > i;
        return '<div class="b3g3p__slot' + (f ? ' b3g3p__slot--paid' : '') + '">' + (f ? '&#x2726;' : '') + '</div>';
      }
      if (b3Count > i) return '<div class="b3g3p__slot b3g3p__slot--free-got">&#x2726;</div>';
      if (b3Count >= 3) return '<div class="b3g3p__slot b3g3p__slot--free-avail">FREE</div>';
      return '<div class="b3g3p__slot b3g3p__slot--free-locked">FREE</div>';
    };
    let b3Pct, b3BarCls, b3Title, b3Msg;
    if (b3Count < 3) {
      const need = 3 - b3Count;
      b3Pct = Math.round(b3Count * 100 / 3);
      b3BarCls = '';
      b3Title = 'Buy 3, Get 3 Free';
      b3Msg = 'Add <strong>' + need + ' more skin' + (need !== 1 ? 's' : '') + '</strong> to unlock 3 FREE';
    } else if (b3Count < 6) {
      const need = 6 - b3Count;
      b3Pct = Math.round((b3Count - 3) * 100 / 3);
      b3BarCls = ' b3g3p__bar-fill--green';
      b3Title = '&#x1F389; 3 Free Skins Unlocked!';
      b3Msg = '<span class="b3g3p__free-msg">Add <strong>' + need + ' more skin' + (need !== 1 ? 's' : '') + '</strong> &#x2014; they\'re FREE!</span>';
    } else {
      b3Pct = 100;
      b3BarCls = ' b3g3p__bar-fill--green';
      b3Title = '&#x1F389; Full deal applied!';
      b3Msg = '<span class="b3g3p__free-msg">3 free skins in your cart &#x2713;</span>';
    }
    const b3g3Bar = [
      '<div class="b3g3p">',
      '<div class="b3g3p__head"><span class="b3g3p__title' + (b3Unlocked ? ' b3g3p__title--gold' : '') + '">' + b3Title + '</span></div>',
      '<div class="b3g3p__slots">',
      '<div class="b3g3p__group">' + [0,1,2].map(mkSlot).join('') + '</div>',
      '<div class="b3g3p__divider"></div>',
      '<div class="b3g3p__group">' + [3,4,5].map(mkSlot).join('') + '</div>',
      '</div>',
      '<div class="b3g3p__bar"><div class="b3g3p__bar-fill' + b3BarCls + '" data-w="' + b3Pct + '" style="width:0%"></div></div>',
      '<p class="b3g3p__msg">' + b3Msg + '</p>',
      '</div>'
    ].join('');

    const nudge = '';

    // Bar 1 — free shipping
    let bar1;
    if (freeRem > 0) {
      const p1 = Math.min(100, Math.round(barTotal * 100 / FREE_CENTS));
      bar1 = [
        '<div class="cart-drawer__shipping-bar">',
        '  <p>Add <strong>' + fmt(freeRem) + '</strong> more for <strong>free shipping</strong></p>',
        '  <div class="shipping-progress"><div class="shipping-progress__fill" data-w="' + p1 + '" style="width:0%"></div></div>',
        '</div>'
      ].join('');
    } else {
      bar1 = '<p class="cart-drawer__shipping-unlocked">&#x2713; Free shipping unlocked!</p>';
    }

    // Bar 2 — tracked + protected
    let bar2;
    if (trackedRem > 0) {
      let barInner;
      if (freeRem <= 0) {
        const range = TRACKED_CENTS - FREE_CENTS;
        const earned = barTotal - FREE_CENTS;
        const p2 = Math.min(100, Math.round(earned * 100 / range));
        barInner = '<p>Add <strong>' + fmt(trackedRem) + '</strong> more to upgrade to <strong>free tracked + protected shipping</strong></p>' +
          '<div class="shipping-progress"><div class="shipping-progress__fill shipping-progress__fill--gold" data-w="' + p2 + '" style="width:0%"></div></div>';
      } else {
        barInner = '<p>Upgrade to <strong>free tracked + protected shipping</strong> on orders over $53.97 USD</p>' +
          '<div class="shipping-progress"><div class="shipping-progress__fill shipping-progress__fill--gold" data-w="0" style="width:0%"></div></div>';
      }
      bar2 = '<div class="cart-drawer__shipping-bar cart-drawer__shipping-bar--gold">' + barInner + '</div>';
    } else {
      bar2 = '<p class="cart-drawer__tracked-unlocked">&#x2713; Free tracked + protected shipping unlocked!</p>';
    }

    footer.innerHTML = b3g3Bar + nudge + bar1 + bar2 + [
      '<div class="cart-drawer__subtotal">',
      '  <span>Subtotal</span>',
      '  <span class="cart-drawer__total">' + fmt(barTotal) + '</span>',
      '</div>',
      '<p class="cart-drawer__tax-note">Taxes and shipping calculated at checkout</p>',
      '<label class="drawer-policy" id="drawer-policy-label">',
      '  <input type="checkbox" id="drawer-policy-check">',
      '  <span class="drawer-policy__box"></span>',
      '  <span class="drawer-policy__text">I agree: <strong>made to order &#x2014; 14-day replacement on defective items only</strong>, no refunds on delivered goods.</span>',
      '</label>',
      '<a href="/checkout" id="drawer-checkout-btn" class="btn btn--primary btn--full btn--large btn--policy-locked">Checkout Securely</a>',
      '<a href="/cart" class="btn btn--ghost btn--full">View Full Cart</a>'
    ].join('');

    initDrawerPolicy(footer);

    // Animate shipping fills (start at 0% → target via CSS transition)
    requestAnimationFrame(() => {
      footer.querySelectorAll('[data-w]').forEach(el => {
        el.style.width = el.dataset.w + '%';
      });
    });
  }

  /* ── Drawer policy checkbox gate ── */
  function initDrawerPolicy(scope) {
    const check = scope.querySelector('#drawer-policy-check');
    const btn   = scope.querySelector('#drawer-checkout-btn');
    const label = scope.querySelector('#drawer-policy-label');
    if (!check || !btn) return;

    function sync() {
      btn.classList.toggle('btn--policy-locked', !check.checked);
      if (label) label.classList.toggle('drawer-policy--checked', check.checked);
    }

    check.addEventListener('change', sync);
    sync();

    btn.addEventListener('click', e => {
      if (!check.checked) {
        e.preventDefault();
        if (label) {
          label.classList.remove('drawer-policy--shake');
          void label.offsetWidth;
          label.classList.add('drawer-policy--shake');
          setTimeout(() => label.classList.remove('drawer-policy--shake'), 450);
        }
      }
    });
  }

  // Wire up the initial Liquid-rendered drawer footer on page load
  const initFooter = document.querySelector('.cart-drawer__footer');
  if (initFooter) initDrawerPolicy(initFooter);

  /* Expose for use in page-level scripts (product-main, bundle-builder) */
  window.ssRefreshCart    = renderCartDrawer;
  window.ssSpawnParticles = spawnParticles;
  window.ssOpenCart       = openCart;

  /* ── Resilient fetch: 8s timeout + 1 automatic retry for mobile network drops ── */
  async function cartFetch(url, opts) {
    for (let attempt = 0; attempt < 2; attempt++) {
      const ctrl  = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 8000);
      try {
        const res = await fetch(url, { ...opts, signal: ctrl.signal });
        clearTimeout(timer);
        return res;
      } catch (err) {
        clearTimeout(timer);
        if (attempt === 1) throw err;
        await new Promise(r => setTimeout(r, 700));
      }
    }
  }

  /* ── Ajax add-to-cart (quick-add buttons on collection pages) ── */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('.product-card__quick-add[data-variant-id]');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.textContent = 'Adding…';
    btn.disabled = true;

    try {
      const res = await cartFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!res.ok) throw new Error('Add to cart failed');

      spawnParticles(btn);
      btn.textContent = 'Added!';
      setTimeout(() => { btn.textContent = 'Add to Cart'; btn.disabled = false; }, 1500);

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        renderCartDrawer(cart);
        openCart();
      } catch { /* cart UI update failed silently */ }
    } catch {
      btn.textContent = 'Error — try again';
      btn.disabled = false;
    }
  });

  /* ── Cart quantity update / remove ── */
  document.addEventListener('click', async e => {
    const qtyBtn    = e.target.closest('.qty-btn');
    const removeBtn = e.target.closest('.cart-item__remove');
    if (!qtyBtn && !removeBtn) return;

    const activeBtn = qtyBtn || removeBtn;
    if (activeBtn.disabled) return;
    activeBtn.disabled = true;

    const cartItem = activeBtn.closest('.cart-item');
    const line     = parseInt(cartItem?.dataset.line, 10);
    if (!line) { activeBtn.disabled = false; return; }

    const currentQty = parseInt(cartItem.querySelector('.qty-value')?.textContent, 10) || 1;
    let newQty;

    if (removeBtn) {
      newQty = 0;
    } else {
      const action = qtyBtn.dataset.action;
      newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
    }

    try {
      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: newQty })
      });
      const cart = await res.json();
      renderCartDrawer(cart);
    } catch { activeBtn.disabled = false; }
  });

  /* ── Announcement bar dismiss ── */
  const annBar   = document.getElementById('announcement-bar');
  const annClose = annBar?.querySelector('.announcement-bar__close');
  const DISMISS_KEY = 'ss_ann_dismissed';

  if (annBar?.dataset.dismissible === 'true') {
    if (sessionStorage.getItem(DISMISS_KEY)) annBar.style.display = 'none';
    annClose?.addEventListener('click', () => {
      annBar.style.display = 'none';
      sessionStorage.setItem(DISMISS_KEY, '1');
    });
  }

  /* ── Countdown timer ── */
  const timerEl = document.getElementById('countdown-timer');
  if (timerEl) {
    const endDate = new Date(timerEl.dataset.end);
    if (!isNaN(endDate)) {
      const tick = () => {
        const diff = endDate - Date.now();
        if (diff <= 0) { timerEl.textContent = 'Offer expired'; return; }
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        timerEl.textContent = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
      };
      tick();
      const _countdownTimer = setInterval(tick, 1000);
    }
  }

  /* ── Newsletter form ── */
  const newsletterForm = document.getElementById('newsletter-form');
  if (newsletterForm) {
    newsletterForm.addEventListener('submit', async e => {
      e.preventDefault();
      const errEl  = document.getElementById('newsletter-error');
      const submit = newsletterForm.querySelector('.newsletter__submit');
      const email  = newsletterForm.querySelector('[name="contact[email]"]').value;

      if (!email) return;

      submit.textContent = 'Subscribing…';
      submit.disabled = true;
      errEl?.removeAttribute('hidden');
      if (errEl) errEl.textContent = '';

      try {
        const res = await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(new FormData(newsletterForm)).toString()
        });
        if (res.ok) {
          submit.textContent = '✓ You\'re in!';
          newsletterForm.querySelector('[name="contact[email]"]').value = '';
        } else {
          throw new Error();
        }
      } catch {
        if (errEl) { errEl.textContent = 'Something went wrong. Please try again.'; errEl.removeAttribute('hidden'); }
        submit.textContent = 'Claim 10% Off';
        submit.disabled = false;
      }
    });
  }

  /* ── GlitterWrap starfield (ported from OriginKit) ── */
  if (!SS_LITE) (function () {
    var canvas = document.getElementById('ss-snow');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Preview props: particleCount:257, color1:"#C6E306", color2:"#3268ED",
    // density:47, starSize:15, focalDepth:2, turbulence:2, brightness:58, glitterIntensity:5
    var PARTICLE_COUNT = 257;
    var COLOR1         = '#C6E306';
    var COLOR2         = '#3268ED';
    var COLOR3         = '#FFE500';
    var STEP_Z         = 5   * 0.0008;  // speed:5       -> 0.004
    var FOCAL_DEPTH    = 2   / 100;     // focalDepth:2  -> 0.02
    var STAR_SCALE     = 15  * 0.15;   // starSize:15   -> 2.25
    var TURBULENCE     = 2   * 0.2;    // turbulence:2  -> 0.4
    var GLITTER        = 5   * 0.1;    // glitterIntensity:5 -> 0.5
    var BRIGHTNESS     = Math.min(1, 15 / 100); // brightness:15 -> 0.15
    var TRAIL          = 100 / 100;    // trailAmount:100 -> 1.0
    var DENSITY        = 47;

    function parseHex(h) {
      var hex = h.trim().slice(1);
      if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
      var n = parseInt(hex, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }

    var palette  = [parseHex(COLOR1), parseHex(COLOR2), parseHex(COLOR3)];
    var rgbStrs  = palette.map(function (c) { return 'rgb('+c[0]+','+c[1]+','+c[2]+')'; });

    var W = 0, H = 0, dpr = 1;
    var stars   = [];
    var elapsed = 0;
    var lastT   = 0;

    function resetStar(s, initial) {
      var angle  = Math.random() * Math.PI * 2;
      var radius = (0.2 + Math.random() * 0.8) * (DENSITY / 15);
      s.x = Math.cos(angle) * radius;
      s.y = Math.sin(angle) * radius;
      s.z = initial ? Math.random() : 1.0;
      s.px = NaN; s.py = NaN;
      s.seed       = Math.random() * 1000;
      s.vmul       = 0.6 + Math.random() * 0.8;
      s.colorIdx   = Math.floor(Math.random() * 3);
      s.flashUntil = 0;
      s.nextFlash  = elapsed + 1 + Math.random() * 4 * (1 / Math.max(0.0001, GLITTER));
    }

    for (var i = 0; i < PARTICLE_COUNT; i++) {
      var s = { x:0, y:0, z:0, px:NaN, py:NaN, seed:0, vmul:1, colorIdx:0, flashUntil:0, nextFlash:0 };
      resetStar(s, true);
      stars.push(s);
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width  = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width  = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
    }

    function drawFrame(deltaSec) {
      var dt = Math.max(0.001, Math.min(0.1, deltaSec)) * 60;
      var cx = W / 2, cy = H / 2;
      var projScale = Math.min(W, H) * 0.9;

      // Fade trail using destination-out so the gradient orb bg shows through
      var keep       = Math.pow(Math.min(0.98, Math.max(0, TRAIL)), dt);
      var trailAlpha = Math.max(0.02, 1 - keep);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,' + trailAlpha + ')';
      ctx.fillRect(0, 0, W, H);

      ctx.globalCompositeOperation = 'lighter';

      for (var i = 0; i < stars.length; i++) {
        var s = stars[i];

        s.z -= STEP_Z * s.vmul * dt;
        if (s.z <= FOCAL_DEPTH) { resetStar(s, false); continue; }

        var tx = s.x, ty = s.y;
        if (TURBULENCE > 0) {
          var tval = elapsed * 1.2 + s.seed;
          var amp  = TURBULENCE * (1 - s.z) * 0.25;
          tx += Math.sin(tval + s.seed) * amp;
          ty += Math.cos(tval * 1.13 + s.seed * 0.7) * amp;
        }

        var persp = FOCAL_DEPTH / Math.max(s.z, 0.0001);
        var sx = cx + tx * persp * projScale;
        var sy = cy + ty * persp * projScale;

        if (sx < -20 || sx > W + 20 || sy < -20 || sy > H + 20) {
          resetStar(s, false); continue;
        }

        var flashMult = 1;
        if (GLITTER > 0) {
          if (elapsed >= s.nextFlash && s.flashUntil < elapsed) {
            s.flashUntil = elapsed + 0.04 + Math.random() * 0.07;
            s.nextFlash  = elapsed + 1 + Math.random() * 4 * (1 / Math.max(0.0001, GLITTER));
          }
          if (elapsed <= s.flashUntil) flashMult = 1 + 2.5 * GLITTER;
        }

        var sizePersp = Math.min(2.5, persp * 0.6);
        var baseR     = Math.max(0.25, STAR_SCALE * (0.4 + sizePersp));
        var maxR      = 1 + STAR_SCALE * 2.5;
        var r         = Math.min(baseR * flashMult, maxR);

        var lifeT = 1 - s.z;
        var a     = Math.min(1, lifeT * 0.9 + 0.05) * BRIGHTNESS * (flashMult > 1 ? 1 : 0.85);

        var colStr = rgbStrs[s.colorIdx];

        if (!isNaN(s.px) && !isNaN(s.py)) {
          ctx.globalAlpha = a * 0.5;
          ctx.strokeStyle = colStr;
          ctx.lineWidth   = Math.max(0.4, r * 0.4);
          ctx.beginPath();
          ctx.moveTo(s.px, s.py);
          ctx.lineTo(sx, sy);
          ctx.stroke();
        }

        ctx.globalAlpha = a;
        ctx.fillStyle   = colStr;
        ctx.fillRect(sx - r, sy - r, r * 2, r * 2);

        if (flashMult > 1) {
          var rf = Math.min(r * 1.4, maxR * 1.4);
          ctx.globalAlpha = a * 0.5;
          ctx.fillRect(sx - rf, sy - rf, rf * 2, rf * 2);
        }

        s.px = sx;
        s.py = sy;
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      elapsed += Math.min(0.1, Math.max(0, deltaSec));
    }

    resize();
    new ResizeObserver(resize).observe(document.documentElement);

    requestAnimationFrame(function loop(t) {
      if (lastT === 0) lastT = t;
      drawFrame((t - lastT) / 1000);
      lastT = t;
      requestAnimationFrame(loop);
    });
  })();

  /* ── Sniper click effect (ported from OriginKit) ── */
  if (!SS_PRM && !SS_LITE) (function () {
    var COLOR    = '#ffffff';
    var DURATION = 300;
    var STROKE   = 2;
    var SIZE     = 90;
    var half     = SIZE / 2;

    document.addEventListener('click', function (e) {
      var x = e.clientX;
      var y = e.clientY;

      // 4 cardinal lines
      [0, 90, 180, 270].forEach(function (deg) {
        var rad = deg * Math.PI / 180;
        var cos = Math.cos(rad);
        var sin = Math.sin(rad);
        var gap = 5, len = SIZE * 0.2;
        var tx = (gap + len) * cos;
        var ty = -(gap + len) * sin;

        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.style.cssText = 'position:fixed;pointer-events:none;overflow:visible;z-index:9999;';
        svg.style.left   = (x - half) + 'px';
        svg.style.top    = (y - half) + 'px';
        svg.style.width  = SIZE + 'px';
        svg.style.height = SIZE + 'px';
        svg.style.transition = 'transform ' + DURATION + 'ms cubic-bezier(0.33,0,0.66,1),'
          + 'opacity ' + (DURATION * 0.4) + 'ms linear ' + (DURATION * 0.6) + 'ms';

        var line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', half + gap * cos);
        line.setAttribute('y1', half - gap * sin);
        line.setAttribute('x2', half + (gap + len) * cos);
        line.setAttribute('y2', half - (gap + len) * sin);
        line.setAttribute('stroke', COLOR);
        line.setAttribute('stroke-width', STROKE);
        line.setAttribute('stroke-linecap', 'square');
        svg.appendChild(line);
        document.body.appendChild(svg);

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            svg.style.transform = 'translate(' + tx + 'px,' + ty + 'px)';
            svg.style.opacity = '0';
          });
        });
        setTimeout(function () { svg.remove(); }, DURATION + 300);
      });

      // 8 diagonal particles
      [Math.PI/3, 2*Math.PI/3, 4*Math.PI/3, 5*Math.PI/3,
       Math.PI/6, 5*Math.PI/6, 7*Math.PI/6, 11*Math.PI/6].forEach(function (angle) {
        var dist = SIZE * 0.4;
        var tx = Math.cos(angle) * dist;
        var ty = Math.sin(angle) * dist;

        var dot = document.createElement('div');
        dot.style.cssText = 'position:fixed;pointer-events:none;border-radius:50%;z-index:9999;'
          + 'background:' + COLOR + ';'
          + 'width:' + STROKE + 'px;height:' + STROKE + 'px;';
        dot.style.left = (x - STROKE / 2) + 'px';
        dot.style.top  = (y - STROKE / 2) + 'px';
        dot.style.transition = 'transform ' + DURATION + 'ms cubic-bezier(0.33,0,0.66,1),'
          + 'opacity ' + (DURATION * 0.4) + 'ms linear ' + (DURATION * 0.6) + 'ms';
        document.body.appendChild(dot);

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            dot.style.transform = 'translate(' + tx + 'px,' + ty + 'px)';
            dot.style.opacity = '0';
          });
        });
        setTimeout(function () { dot.remove(); }, DURATION + 300);
      });
    });
  })();

  /* ── Lightning bolt background animation ── */
  if (!SS_LITE) (function() {
    function generateBolt(x1, y1, x2, y2, roughness, depth) {
      if (depth === 0) return [[x1, y1], [x2, y2]];
      const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * roughness;
      const my = (y1 + y2) / 2;
      return generateBolt(x1, y1, mx, my, roughness * 0.6, depth - 1)
        .slice(0, -1)
        .concat(generateBolt(mx, my, x2, y2, roughness * 0.6, depth - 1));
    }

    function paintBolt(ctx, pts, branches) {
      function seg(points, color, lw, blur) {
        ctx.beginPath();
        ctx.moveTo(points[0][0], points[0][1]);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i][0], points[i][1]);
        ctx.strokeStyle = color;
        ctx.lineWidth = lw;
        ctx.lineCap = 'round';
        ctx.shadowColor = '#90c8f8';
        ctx.shadowBlur = blur;
        ctx.stroke();
      }
      seg(pts, 'rgba(140, 200, 255, 0.28)', 8, 20);
      seg(pts, 'rgba(220, 242, 255, 0.88)', 1.2, 6);
      if (branches) branches.forEach((b, i) => {
        const isPrimary = i % 2 === 0;
        seg(b, isPrimary ? 'rgba(170, 218, 255, 0.32)' : 'rgba(155, 210, 255, 0.22)', isPrimary ? 5 : 3, isPrimary ? 14 : 8);
        seg(b, isPrimary ? 'rgba(215, 240, 255, 0.78)' : 'rgba(200, 235, 255, 0.55)', isPrimary ? 0.9 : 0.6, isPrimary ? 5 : 3);
      });
      ctx.shadowBlur = 0;
    }

    function initCanvas(canvas) {
      const ctx = canvas.getContext('2d');
      let tid = null, rafId = null;

      function resize() {
        const w = canvas.offsetWidth || window.innerWidth;
        const h = canvas.offsetHeight || window.innerHeight;
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;
      }

      function makeBolt() {
        const W = canvas.width, H = canvas.height;
        if (!W || !H) return null;
        const x  = W * (0.15 + Math.random() * 0.7);
        const sy = H * Math.random() * 0.06;
        const ey = H * (0.45 + Math.random() * 0.45);
        const ex = x + (Math.random() - 0.5) * W * 0.45;
        const pts = generateBolt(x, sy, ex, ey, W * 0.2, 5);

        // 2-4 primary branches + sub-branches off each
        const branches = [];
        const numPrimary = 2 + Math.floor(Math.random() * 3);
        for (let b = 0; b < numPrimary; b++) {
          const bi = Math.floor(pts.length * 0.1 + Math.random() * pts.length * 0.6);
          const bx = pts[bi][0] + (Math.random() - 0.5) * W * 0.4;
          const by = Math.min(pts[bi][1] + Math.random() * (ey - pts[bi][1]) * 0.85 + 30, H - 5);
          const bPts = generateBolt(pts[bi][0], pts[bi][1], bx, by, W * 0.12, 4);
          branches.push(bPts);
          // sub-branch off this primary
          if (bPts.length > 4 && Math.random() > 0.4) {
            const sbi = Math.floor(bPts.length * 0.25 + Math.random() * bPts.length * 0.4);
            branches.push(generateBolt(
              bPts[sbi][0], bPts[sbi][1],
              bPts[sbi][0] + (Math.random() - 0.5) * W * 0.22,
              Math.min(bPts[sbi][1] + Math.random() * (by - bPts[sbi][1]) * 0.7, H - 5),
              W * 0.07, 3
            ));
          }
        }
        return { pts, branches };
      }

      function strike() {
        const bolt = makeBolt();
        if (!bolt) { scheduleNext(); return; }
        let t0 = null;
        function step(ts) {
          if (!t0) t0 = ts;
          const e = ts - t0;
          const a = e < 65  ? e / 65
                  : e < 130 ? 1 - (e - 65) / 130
                  : e < 200 ? (e - 130) / 90
                  : Math.max(0, 1 - (e - 200) / 400);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          if (a > 0.02) {
            ctx.save();
            ctx.globalAlpha = a * 0.42;
            paintBolt(ctx, bolt.pts, bolt.branches);
            ctx.restore();
          }
          rafId = e < 600 ? requestAnimationFrame(step) : (ctx.clearRect(0,0,canvas.width,canvas.height), scheduleNext(), null);
        }
        rafId = requestAnimationFrame(step);
      }

      function scheduleNext() {
        tid = setTimeout(strike, 4500 + Math.random() * 8000);
      }

      resize();
      if (canvas.parentElement) new ResizeObserver(resize).observe(canvas.parentElement);
      tid = setTimeout(strike, 1500 + Math.random() * 3500);
    }

    if (window.innerWidth > 768) document.querySelectorAll('.lightning-canvas').forEach(initCanvas);
  })();

})();
